import { tt } from '@agos/shared'

/**
 * Which ways the franchise will take money, and for which kind of order.
 *
 * **Measured: `{"isActive": true, "defaultOnline": false, "allowOnlinePickup": true}`.** So all three
 * methods are on offer today — which is exactly why this being absent went unnoticed: the port offered
 * all three unconditionally and happened to be right.
 *
 * Two settings can take one away, and both are a franchise's own switch:
 *
 * - **`isActive`** turns card-online off entirely. Offering it anyway sends the customer to a payment
 *   gateway the restaurant has not configured, and the order is placed and unpaid at the far end.
 * - **`allowOnlinePickup`** turns it off for **pickup** orders only. A restaurant may be happy to take
 *   a card for a delivery it has to send a driver on and not for a bag somebody collects.
 *
 * `defaultOnline` picks which method starts selected. It is not a restriction and does not belong with
 * the other two, but legacy reads all three from one payload and so does this.
 */

/**
 * §9.766, owner O127 - `qr_pay` is *QR platba*: chosen here, paid at the door with the QR the driver shows (BACKEND.md
 * §10A.418, §10A.127). Nothing is paid in the app.
 */
export type PaymentMethod = 'cash' | 'card_terminal' | 'card_online' | 'qr_pay'
/**
 * Whether the order goes to an address — §9.50.
 *
 * This was `type: 'delivery' | 'bar'`, a slug, and card-online was offered when it read `'delivery'`.
 * With the channels coming from the franchise that is the wrong question: a *Terasa* would have been
 * treated as neither. What the rule always meant is *is this delivered*, which is the channel's own
 * `requiresDeliveryAddress`, so the capability is passed instead of the name.
 */
export type OrderType = 'delivery' | 'bar'

export interface PaymentSettings {
  /** Whether card-online is offered at all. */
  onlineActive: boolean
  /** Whether card-online is offered on a pickup order. */
  onlineOnPickup: boolean
  /** Whether card-online is the method that starts selected. */
  defaultOnline: boolean
  /** §9.766 - whether *Karta - terminál* is offered; the franchise can switch it off for customers. */
  cardTerminalOffered: boolean
  /**
   * §9.766 - whether *QR platba* can be offered: switched on **and** honourable now (the server's `qrPayAvailable`,
   * which asks the rule the driver's QR button reads). Offered on a delivery only - BACKEND.md §10A.418 point 5.
   */
  qrAvailable: boolean
}

export const PAYMENT_FALLBACK: PaymentSettings = {
  // Fails **closed** for the one method that can strand an order: a settings call that did not answer
  // is not a reason to send somebody to a gateway that may not exist. Cash and the terminal are always
  // available — they need nothing configured.
  onlineActive: false,
  onlineOnPickup: true,
  defaultOnline: false,
  // §9.766 - the terminal stays: it is offered by default and the server is the authority, so an unanswered read is
  // not a reason to take it away. QR fails closed with card online: a code nobody may be able to show is the same
  // stranded order as a gateway nobody configured.
  cardTerminalOffered: true,
  qrAvailable: false,
}

export function paymentSettingsOf(payload: unknown): PaymentSettings {
  if (typeof payload !== 'object' || payload === null) return PAYMENT_FALLBACK
  const row = payload as Record<string, unknown>
  return {
    onlineActive: row.isActive === true,
    // Legacy's `?? true`: the field was added after the feature, so absent means *before anybody
    // chose*, not *off*. It only matters when `isActive` is true anyway.
    onlineOnPickup: row.allowOnlinePickup !== false,
    defaultOnline: row.defaultOnline === true,
    // §9.766 - *absent is not false*: a server older than §10A.418 sends no field, and it offered the terminal.
    cardTerminalOffered: row.offerCardTerminal !== false,
    // Only an explicit `true` offers a QR, for `isActive`'s reason: it promises something somebody has to show.
    qrAvailable: row.qrPayAvailable === true,
  }
}

/**
 * The methods to offer, in the order they are shown.
 *
 * **§9.766, owner O127** - *Karta - terminál* unless the franchise switched it off, and *QR platba* on a delivery when
 * the server says a QR can be honoured. **On a delivery only**: the driver shows the QR at the door, and nothing at a
 * pickup counter can (BACKEND.md §10A.418 point 5) - the server refuses a pickup's QR with the same reason, so this
 * and `CustomerPaymentMethods.requireOffered` carry the one condition each and change together.
 */
export function paymentMethodsFor(settings: PaymentSettings, delivered: boolean): PaymentMethod[] {
  const methods: PaymentMethod[] = ['cash']
  if (settings.cardTerminalOffered) methods.push('card_terminal')
  if (settings.onlineActive && (delivered || settings.onlineOnPickup)) {
    methods.push('card_online')
  }
  if (settings.qrAvailable && delivered) methods.push('qr_pay')
  return methods
}

/**
 * Which method should be selected, given what is on offer and what was selected before.
 *
 * The reason this is a function rather than an initial value: **the order type can change after the
 * method was picked.** A customer choosing card-online for a delivery and then switching to pickup, at
 * a restaurant that does not take cards on pickup, would otherwise be left holding a method that is no
 * longer offered — and legacy has an effect that resets it for exactly this case.
 */
export function selectedPaymentMethod(
  settings: PaymentSettings,
  delivered: boolean,
  current: PaymentMethod | null,
): PaymentMethod {
  const offered = paymentMethodsFor(settings, delivered)
  if (current !== null && offered.includes(current)) return current
  if (current === null && settings.defaultOnline && offered.includes('card_online')) return 'card_online'
  return offered[0] ?? 'cash'
}

/** What was picked and what will actually be used - §9.479. */
export interface MethodSubstitution {
  chose: PaymentMethod
  using: PaymentMethod
}

/**
 * Whether {@link selectedPaymentMethod} is about to replace the customer's choice - §9.479, owner R3.
 *
 * **The swap is right and silent, and the silence is the defect.** `paymentSettingsOf(undefined)` is
 * `PAYMENT_FALLBACK`, so a public settings read that stops answering takes `card_online` out of the offered
 * list, its tile off the screen, and the submit places the order as **cash** - under a method the customer
 * did not pick, with nothing said. `chosenPayment` still holds what they chose throughout.
 *
 * **Derived from `selectedPaymentMethod` rather than restating its rule**, so a change to which methods are
 * offered cannot leave the notice describing the old behaviour. That matters more than it looks: the two
 * legitimate reasons to substitute - the order type changing and the settings failing closed - are one line
 * of code, and a second copy of the condition would have to keep agreeing with it.
 */
export function substitutedPaymentMethod(
  settings: PaymentSettings,
  delivered: boolean,
  current: PaymentMethod | null,
): MethodSubstitution | null {
  // Nothing chosen yet is not a substitution: the default was never the customer's decision to lose.
  if (current === null) return null
  const using = selectedPaymentMethod(settings, delivered, current)
  return using === current ? null : { chose: current, using }
}

/**
 * Whether the payment settings are unknown rather than known-to-be-off - §9.501.
 *
 * **§9.479 made the swap speak and gave both situations the same sentence.** `paymentSettingsOf(undefined)` is
 * `PAYMENT_FALLBACK`, so a read that answered *card is off* and a read that did not answer at all are
 * indistinguishable downstream - and the right thing to say differs: one is a fact about the franchise, the
 * other is a gap in what this page knows, and only the second has anything the customer can do about it.
 *
 * **Measured, which is why it is worth a branch**: card online ran at about 27 % of delivery and pickup orders
 * to 19 September and about 11 % after, on flat total volume, while card at the door rose. Customers were not
 * lost, they were pushed to another method - so a sentence that says *card is unavailable* when the truth is
 * *we could not check* costs a card payment every time.
 *
 * <p><b>`failed` with data is not unknown.</b> The customer app keeps a 60 s `staleTime`, so a refetch can
 * fail while a perfectly good earlier answer is still in hand. Treating that as unknown would hide a card tile
 * the franchise really does offer.
 */
export function paymentSettingsUnknown(payload: unknown, failed: boolean): boolean {
  return failed && (payload === undefined || payload === null)
}

/**
 * The sentence when the settings could not be read at all - §9.501.
 *
 * **Says what is not known rather than asserting a fact**, and names the method that is being used meanwhile so
 * the customer is not guessing what they will be charged by - which is §9.479's rule and the reason that entry
 * names both methods.
 */
export function paymentUnknownNote(using: PaymentMethod): string {
  // §9.766 - QR fails closed with card online, so the sentence names both rather than half of what is unknown.
  return `${tt('Nepodarilo sa zistiť, či je dostupná online platba kartou a QR platba. Objednávka bude na')} ${paymentMethodLabel(using)}.`
}

/**
 * The sentence, naming **both** methods - §9.479.
 *
 * Saying only *card is unavailable* would leave the customer guessing what they are about to be charged by,
 * which on this screen is the whole question: the tile they pressed has gone and another one is lit.
 */
export function paymentSubstitutionNote(substitution: MethodSubstitution): string {
  return `${paymentMethodLabel(substitution.chose)} ${tt('momentálne nie je k dispozícii, objednávka bude na')} ${paymentMethodLabel(substitution.using)}.`
}

/**
 * The method's name. **No `default` since §9.766**: the fourth method was going to fall through to *Online platba
 * kartou*, so every method is named and a fifth is TS2366 rather than a QR drawn as a card.
 */
export function paymentMethodLabel(method: PaymentMethod): string {
  switch (method) {
    case 'cash':
      return tt('Hotovosť')
    case 'card_terminal':
      // Legacy's own name for it. This port called it *Kartou pri prevzatí*, which is legacy's
      // *description* of the same method — so the label and the line under it were the same sentence
      // twice, and neither matched what legacy calls the thing (FB-C53).
      return tt('Karta – terminál')
    case 'card_online':
      return tt('Online platba kartou')
    case 'qr_pay':
      return tt('QR platba')
  }
}

/** The line under the name. Legacy has one for each, and they are not decoration: *na bare* is where. No `default`. */
export function paymentMethodNote(method: PaymentMethod): string {
  switch (method) {
    case 'cash':
      return tt('Platba kuriérovi / na bare')
    case 'card_terminal':
      return tt('Platba kartou pri prevzatí')
    case 'card_online':
      return tt('Zabezpečená online platobná brána')
    case 'qr_pay':
      // §9.766 - where and how, because nothing happens in the app: the courier shows the code at the door.
      return tt('Kuriér vám pri dverách ukáže QR kód na zaplatenie')
  }
}
